// ============================================================================
// ADR-0024 — "Insert data" picker for the narrative editor toolbar
// ============================================================================
// One toolbar button opening a searchable popover of data-bound fields
// (grouped, each showing its current value) and data tables.
//
// Rendered through a portal on document.body with position: fixed, clamped
// to the viewport — a native <select> with ~30 grouped options ran off the
// screen and could not be scrolled comfortably inside the report modal, and
// an in-modal absolutely-positioned popover would be clipped by the modal's
// overflow. The list height adapts to the space below (or above) the button.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReportFieldOption } from 'api/types/fsa/reportData.types';

export interface DataInsertTable {
  source: string;
  label: string;
}

interface Props {
  fields: ReportFieldOption[];
  tables: DataInsertTable[];
  onInsertField: (field: ReportFieldOption) => void;
  onInsertTable: (table: DataInsertTable) => void;
}

const POPOVER_WIDTH = 380;
const MARGIN = 8;

// Row hover highlight (inline styles cannot express :hover).
const PICKER_STYLE_ID = 'fsa-data-insert-picker-styles';
if (typeof document !== 'undefined' && !document.getElementById(PICKER_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = PICKER_STYLE_ID;
  style.textContent = `.fsa-data-picker-item:hover, .fsa-data-picker-item:focus-visible { background: var(--bg-secondary, #f1f5f9) !important; outline: none; }`;
  document.head.appendChild(style);
}

const DataInsertPicker: React.FC<Props> = ({ fields, tables, onInsertField, onInsertTable }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);

  // Place below the button when there is room, else above; clamp horizontally.
  const place = useCallback(() => {
    const r = buttonRef.current?.getBoundingClientRect();
    if (!r) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(MARGIN, Math.min(r.left, vw - POPOVER_WIDTH - MARGIN));
    const below = vh - r.bottom - MARGIN * 2;
    const above = r.top - MARGIN * 2;
    if (below >= 260 || below >= above) {
      setPos({ top: r.bottom + 4, left, maxHeight: Math.max(160, below) });
    } else {
      const maxHeight = Math.max(160, above);
      setPos({ top: Math.max(MARGIN, r.top - 4 - maxHeight), left, maxHeight });
    }
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onReflow = () => place();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, close, place]);

  const q = query.trim().toLowerCase();
  const matchedFields = useMemo(
    () => fields.filter(f => !q
      || f.label.toLowerCase().includes(q)
      || f.group.toLowerCase().includes(q)
      || (f.value ?? '').toLowerCase().includes(q)),
    [fields, q],
  );
  const groups = useMemo(() => {
    const m = new Map<string, ReportFieldOption[]>();
    for (const f of matchedFields) {
      const list = m.get(f.group) ?? [];
      list.push(f);
      m.set(f.group, list);
    }
    return Array.from(m.entries());
  }, [matchedFields]);
  const matchedTables = useMemo(
    () => tables.filter(t => !q || t.label.toLowerCase().includes(q)),
    [tables, q],
  );

  const pickField = (f: ReportFieldOption) => { onInsertField(f); close(); };
  const pickTable = (t: DataInsertTable) => { onInsertTable(t); close(); };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (matchedFields[0]) pickField(matchedFields[0]);
    else if (matchedTables[0]) pickTable(matchedTables[0]);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={e => e.preventDefault()}
        onClick={() => (open ? close() : setOpen(true))}
        style={{ ...triggerStyle, background: open ? 'var(--bg-primary)' : 'transparent' }}
        title="Insert a value or table that stays in step with the engagement data"
      >
        <span style={{ fontWeight: 700, color: '#0369a1' }}>{'{ }'}</span>
        <span>Insert data</span>
        <span style={{ fontSize: '0.6rem' }}>▾</span>
      </button>

      {open && pos && createPortal(
        <div
          ref={popoverRef}
          style={{ ...popoverStyle, top: pos.top, left: pos.left, maxHeight: pos.maxHeight }}
          onMouseDown={e => { if ((e.target as HTMLElement).tagName !== 'INPUT') e.preventDefault(); }}
        >
          <div style={searchWrapStyle}>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Search fields and tables…"
              style={searchStyle}
            />
          </div>
          <div style={listStyle}>
            {groups.map(([group, items]) => (
              <div key={group}>
                <div style={groupHeaderStyle}>{group}</div>
                {items.map(f => (
                  <button key={f.path} type="button" className="fsa-data-picker-item" style={itemStyle} onClick={() => pickField(f)} title={f.path}>
                    <span style={itemLabelStyle}>{f.label}</span>
                    <span style={f.value ? itemValueStyle : itemNoValueStyle}>
                      {f.value || 'no value yet'}
                    </span>
                  </button>
                ))}
              </div>
            ))}
            {matchedTables.length > 0 && (
              <div>
                <div style={groupHeaderStyle}>Tables</div>
                {matchedTables.map(t => (
                  <button key={t.source} type="button" className="fsa-data-picker-item" style={itemStyle} onClick={() => pickTable(t)}>
                    <span style={itemLabelStyle}>▦ {t.label}</span>
                    <span style={itemValueStyle}>rows built from engagement data</span>
                  </button>
                ))}
              </div>
            )}
            {groups.length === 0 && matchedTables.length === 0 && (
              <div style={emptyStyle}>Nothing matches “{query}”.</div>
            )}
          </div>
          <div style={footerStyle}>
            {matchedFields.length} field{matchedFields.length === 1 ? '' : 's'} · {matchedTables.length} table{matchedTables.length === 1 ? '' : 's'} · Enter inserts the first match · Esc closes
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

const triggerStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  height: 26, padding: '0 8px',
  border: '1px solid var(--border-color)', borderRadius: 4,
  color: 'var(--text-primary)', fontSize: '0.72rem', fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap',
};
const popoverStyle: React.CSSProperties = {
  position: 'fixed',
  // Above modals (--z-modal: 10050); same layer as the app's other
  // portaled menus (SyutSelect / SyutTreeSelect menuPortal: 10200).
  zIndex: 10200,
  width: POPOVER_WIDTH,
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-primary, #fff)',
  border: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: 8,
  boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
  overflow: 'hidden',
};
const searchWrapStyle: React.CSSProperties = {
  padding: 8,
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  flexShrink: 0,
};
const searchStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 8px',
  fontSize: '0.8rem',
  border: '1px solid var(--form-border-color, #cbd5e1)',
  borderRadius: 4,
  background: 'var(--bg-primary, #fff)',
  color: 'var(--text-primary)',
};
const listStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '4px 0',
};
const groupHeaderStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  padding: '6px 12px 3px',
  fontSize: '0.64rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--text-secondary, #64748b)',
  background: 'var(--bg-primary, #fff)',
};
const itemStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 1,
  width: '100%',
  padding: '5px 12px',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
};
const itemLabelStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  color: 'var(--text-primary)',
  fontWeight: 500,
};
const itemValueStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary, #64748b)',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const itemNoValueStyle: React.CSSProperties = { ...itemValueStyle, color: '#b45309', fontStyle: 'italic' };
const emptyStyle: React.CSSProperties = {
  padding: '16px 12px',
  fontSize: '0.78rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};
const footerStyle: React.CSSProperties = {
  flexShrink: 0,
  padding: '5px 12px',
  borderTop: '1px solid var(--border-color, #e2e8f0)',
  fontSize: '0.64rem',
  color: 'var(--text-secondary, #64748b)',
  background: 'var(--bg-secondary, #f8fafc)',
};

export default DataInsertPicker;
